import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ContentContext = createContext(null);

const EMPTY_CONTENT = { heroSlides: [], roster: [], element45: null };

const ContentErrorFallback = ({ onRetry }) => (
  <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="content-error-fallback">
    <div className="text-center max-w-md">
      <img src="/rhodium-logo.png" alt="Rhodium Records" className="h-24 w-24 object-contain mx-auto mb-6" />
      <h1 className="text-white text-2xl font-black mb-3">Something went wrong</h1>
      <p className="text-white/60 text-base mb-8">
        We couldn't load the site content. Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="px-8 py-4 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
        data-testid="content-retry-btn"
      >
        Retry
      </button>
    </div>
  </div>
);

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [slidesRes, rosterRes, e45Res] = await Promise.all([
        axios.get(`${API}/api/content/hero-slides`),
        axios.get(`${API}/api/content/roster`),
        axios.get(`${API}/api/content/element45`)
      ]);
      setContent({ heroSlides: slidesRes.data, roster: rosterRes.data, element45: e45Res.data });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ ...content, loading, error, reload: load }),
    [content, loading, error, load]
  );

  return (
    <ContentContext.Provider value={value}>
      {error && !loading ? <ContentErrorFallback onRetry={load} /> : children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
